import { httpsCallable } from "firebase/functions";
import { useMemo, useState } from "react";
import { functions } from "../firebase";

type PurgeSelections = {
  messaging: boolean;
  clubs: boolean;
  news: boolean;
  calendar: boolean;
  volunteer: boolean;
  auditLogs: boolean;
  rosterStudents: boolean;
  rosterParents: boolean;
  rosterTeachers: boolean;
  rosterFamilies: boolean;
  rosterLinks: boolean;
  userProfiles: boolean;
};

type PurgeResponse = {
  mode: "granular";
  dryRun: boolean;
  limit: number;
  selections: PurgeSelections;
  scanned: Record<string, number>;
  deleted: Record<string, number>;
  notes: string[];
};

function prettyJson(obj: any) {
  return JSON.stringify(obj, null, 2);
}

function emptySelections(): PurgeSelections {
  return {
    messaging: false,
    clubs: false,
    news: false,
    calendar: false,
    volunteer: false,
    auditLogs: false,
    rosterStudents: false,
    rosterParents: false,
    rosterTeachers: false,
    rosterFamilies: false,
    rosterLinks: false,
    userProfiles: false,
  };
}

function betaResetSelections(): PurgeSelections {
  return {
    messaging: true,
    clubs: true,
    news: true,
    calendar: true,
    volunteer: true,
    auditLogs: false,
    rosterStudents: true,
    rosterParents: true,
    rosterTeachers: true,
    rosterFamilies: true,
    rosterLinks: true,
    userProfiles: false,
  };
}

function contentOnlySelections(): PurgeSelections {
  return {
    messaging: true,
    clubs: true,
    news: true,
    calendar: true,
    volunteer: true,
    auditLogs: false,
    rosterStudents: false,
    rosterParents: false,
    rosterTeachers: false,
    rosterFamilies: false,
    rosterLinks: false,
    userProfiles: false,
  };
}

function rosterOnlySelections(): PurgeSelections {
  return {
    messaging: false,
    clubs: false,
    news: false,
    calendar: false,
    volunteer: false,
    auditLogs: false,
    rosterStudents: true,
    rosterParents: true,
    rosterTeachers: true,
    rosterFamilies: true,
    rosterLinks: true,
    userProfiles: false,
  };
}

function fullResetSelections(): PurgeSelections {
  return {
    messaging: true,
    clubs: true,
    news: true,
    calendar: true,
    volunteer: true,
    auditLogs: true,
    rosterStudents: true,
    rosterParents: true,
    rosterTeachers: true,
    rosterFamilies: true,
    rosterLinks: true,
    userProfiles: true,
  };
}

export function DataPurge() {
  const [limit, setLimit] = useState<number>(1000);
  const [selections, setSelections] = useState<PurgeSelections>(betaResetSelections());

  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const [result, setResult] = useState<PurgeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const confirmPhrase = "PURGE SELECTED DATA";
  const confirmOk = confirmText.trim().toUpperCase() === confirmPhrase;
  const selectedCount = Object.values(selections).filter(Boolean).length;

  const summaryRows = useMemo(() => {
    if (!result) return [];
    const keys = Array.from(
      new Set([...Object.keys(result.scanned || {}), ...Object.keys(result.deleted || {})])
    ).sort();
    return keys.map((k) => ({
      name: k,
      scanned: result.scanned?.[k] ?? 0,
      deleted: result.deleted?.[k] ?? 0,
    }));
  }, [result]);

  function setPreset(name: "clear" | "beta" | "content" | "roster" | "full") {
    if (name === "clear") setSelections(emptySelections());
    if (name === "beta") setSelections(betaResetSelections());
    if (name === "content") setSelections(contentOnlySelections());
    if (name === "roster") setSelections(rosterOnlySelections());
    if (name === "full") setSelections(fullResetSelections());

    setConfirmText("");
    setResult(null);
    setError(null);
  }

  function toggleSelection(key: keyof PurgeSelections) {
    setSelections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
    setConfirmText("");
    setResult(null);
    setError(null);
  }

  async function run(dryRun: boolean) {
    if (selectedCount === 0) {
      setError("Select at least one purge target.");
      setResult(null);
      return;
    }

    setBusy(true);
    setError(null);

    try {
      const fn = httpsCallable(functions, "purgeData");
      const res: any = await fn({
        dryRun,
        limit,
        selections,
      });
      setResult(res?.data as PurgeResponse);
    } catch (e: any) {
      setError(e?.message || "Request failed.");
      setResult(null);
    } finally {
      setBusy(false);
    }
  }

  async function runLive() {
    if (!confirmOk || selectedCount === 0) return;

    const finalAck = window.confirm(
      "Final confirmation: permanently purge the selected app data? Admin and Super Admin accounts will be preserved by the backend safeguards."
    );
    if (!finalAck) return;

    await run(false);
  }

  return (
    <div className="card">
      <h2>Data Purge — Super Admin Tool</h2>
      <p className="muted">
        This tool performs destructive cleanup for selected app data.
        <b> Use Dry Run first.</b>
      </p>
      <p className="muted">
        Firebase Auth users are not deleted here. Admin and Super Admin user profiles are preserved automatically by the backend.
      </p>

      <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
        <div style={{ border: "1px solid #eee", borderRadius: 12, padding: 12, background: "#fafafa" }}>
          <div style={{ fontWeight: 800, marginBottom: 8 }}>Presets</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" className="btn secondary" onClick={() => setPreset("beta")}>
              Content + Roster Reset
            </button>
            <button type="button" className="btn secondary" onClick={() => setPreset("content")}>
              Content Only
            </button>
            <button type="button" className="btn secondary" onClick={() => setPreset("roster")}>
              Roster Only
            </button>
            <button type="button" className="btn secondary" onClick={() => setPreset("full")}>
              Full Reset
            </button>
            <button type="button" className="btn secondary" onClick={() => setPreset("clear")}>
              Clear All Checks
            </button>
          </div>
          <div className="muted" style={{ marginTop: 8 }}>
            Content + Roster Reset clears operational content and roster data while preserving account access.
          </div>
        </div>

        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ border: "1px solid #eee", borderRadius: 12, padding: 12 }}>
            <div style={{ fontWeight: 800, marginBottom: 8 }}>Content</div>
            <div style={{ display: "grid", gap: 8 }}>
              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={selections.messaging}
                  onChange={() => toggleSelection("messaging")}
                />
                <span>Messaging (threads, messages, inbox index)</span>
              </label>

              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={selections.clubs}
                  onChange={() => toggleSelection("clubs")}
                />
                <span>Clubs</span>
              </label>

              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={selections.news}
                  onChange={() => toggleSelection("news")}
                />
                <span>News</span>
              </label>

              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={selections.calendar}
                  onChange={() => toggleSelection("calendar")}
                />
                <span>Calendar</span>
              </label>

              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={selections.volunteer}
                  onChange={() => toggleSelection("volunteer")}
                />
                <span>Volunteer</span>
              </label>

              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={selections.auditLogs}
                  onChange={() => toggleSelection("auditLogs")}
                />
                <span>Audit Logs</span>
              </label>
            </div>
          </div>

          <div style={{ border: "1px solid #eee", borderRadius: 12, padding: 12 }}>
            <div style={{ fontWeight: 800, marginBottom: 8 }}>Roster</div>
            <div style={{ display: "grid", gap: 8 }}>
              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={selections.rosterStudents}
                  onChange={() => toggleSelection("rosterStudents")}
                />
                <span>Students</span>
              </label>

              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={selections.rosterParents}
                  onChange={() => toggleSelection("rosterParents")}
                />
                <span>Parents</span>
              </label>

              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={selections.rosterTeachers}
                  onChange={() => toggleSelection("rosterTeachers")}
                />
                <span>Teachers / Staff</span>
              </label>

              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={selections.rosterFamilies}
                  onChange={() => toggleSelection("rosterFamilies")}
                />
                <span>Families</span>
              </label>

              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={selections.rosterLinks}
                  onChange={() => toggleSelection("rosterLinks")}
                />
                <span>Roster Links / Assignments</span>
              </label>
            </div>
          </div>

          <div style={{ border: "1px solid #eee", borderRadius: 12, padding: 12, background: "#fff8f8" }}>
            <div style={{ fontWeight: 800, marginBottom: 8 }}>Accounts / Identity</div>
            <div style={{ display: "grid", gap: 8 }}>
              <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="checkbox"
                  checked={selections.userProfiles}
                  onChange={() => toggleSelection("userProfiles")}
                />
                <span>User Profiles</span>
              </label>
            </div>
            <div className="muted" style={{ marginTop: 8 }}>
              Admin and Super Admin profiles are preserved automatically even if this box is checked.
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            Limit
            <input
              type="number"
              min={1}
              max={5000}
              value={limit}
              onChange={(e) => setLimit(parseInt(e.target.value || "1000", 10))}
              style={{ width: 110, padding: "8px 10px", borderRadius: 10, border: "1px solid #ddd" }}
            />
          </label>

          <div className="muted">Selected groups: <b>{selectedCount}</b></div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button className="btn secondary" disabled={busy || selectedCount === 0} onClick={() => run(true)}>
            {busy ? "Working…" : "Dry Run"}
          </button>

          <button
            className="btn"
            disabled={busy || !confirmOk || selectedCount === 0}
            onClick={runLive}
            style={{
              opacity: busy || !confirmOk || selectedCount === 0 ? 0.6 : 1,
              cursor: busy || !confirmOk || selectedCount === 0 ? "not-allowed" : "pointer",
              background: "#b00020",
            }}
          >
            {busy ? "Working…" : "PURGE NOW"}
          </button>
        </div>

        <div style={{ border: "1px solid #eee", borderRadius: 12, padding: 12, background: "#fafafa" }}>
          <div style={{ fontWeight: 800, marginBottom: 6 }}>Confirmation required</div>

          <div className="muted" style={{ marginBottom: 8 }}>
            To continue, type exactly: <code>{confirmPhrase}</code>
          </div>

          <input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={`Type ${confirmPhrase}`}
            style={{
              width: "100%",
              padding: "10px 12px",
              borderRadius: 10,
              border: "1px solid #ddd",
              outline: "none",
            }}
          />

          <div className="muted" style={{ marginTop: 8, fontSize: 13 }}>
            Calls Cloud Function <code>purgeData</code>. Firebase Auth users are not deleted here.
            Admin and Super Admin profiles are preserved by the backend safeguards.
          </div>
        </div>

        {error ? <div style={{ color: "#b00020", fontWeight: 800 }}>{error}</div> : null}

        {result ? (
          <div style={{ marginTop: 6 }}>
            <div style={{ fontWeight: 900 }}>Result</div>
            <div className="muted" style={{ marginTop: 6 }}>
              Dry run: <b>{String(result.dryRun)}</b> • Limit: <b>{result.limit}</b>
            </div>

            <div style={{ overflowX: "auto", marginTop: 12 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr>
                    {["Collection", "Scanned", "Deleted"].map((h) => (
                      <th key={h} style={{ textAlign: "left", borderBottom: "1px solid #eee", padding: "8px 6px" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {summaryRows.map((r) => (
                    <tr key={r.name}>
                      <td style={{ borderBottom: "1px solid #f2f2f2", padding: "8px 6px" }}>
                        <code>{r.name}</code>
                      </td>
                      <td style={{ borderBottom: "1px solid #f2f2f2", padding: "8px 6px" }}>
                        {r.scanned}
                      </td>
                      <td style={{ borderBottom: "1px solid #f2f2f2", padding: "8px 6px" }}>
                        {r.deleted}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {result.notes?.length ? (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontWeight: 800, marginBottom: 6 }}>Notes</div>
                <ul style={{ marginTop: 0 }}>
                  {result.notes.map((n, i) => (
                    <li key={i} className="muted">{n}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            <details style={{ marginTop: 12 }}>
              <summary style={{ cursor: "pointer", fontWeight: 800 }}>Raw response JSON</summary>
              <pre style={{ marginTop: 10, padding: 12, background: "#f7f7f7", borderRadius: 10, overflowX: "auto" }}>
                {prettyJson(result)}
              </pre>
            </details>
          </div>
        ) : null}
      </div>
    </div>
  );
}
